/**
 * Situations — situational ESL phrasebook types.
 *
 * Sibling type to `EnglishCardExt`, NOT an extension. A Situation is a
 * sentence card (front = "Where is the restroom?"), not a lemma — POS,
 * IPA, freq, and example.sourceUrl don't apply. The shared base
 * `Card<TExt>` keeps lang-core SRS signals working with both kinds of
 * cards via separate `createSrsSignals<TExt>` instances.
 *
 * Schema decisions documented in
 * /Users/kevinnguyen/.claude/plans/1-i-think-practice-tingly-gizmo.md
 * (DEEPEN + BIG CHANGE review):
 *   - speechAct as KNOWN_SPEECH_ACTS const array → flexible content authoring
 *   - pragmaticNote → explicit J↔E politeness mismatch callouts (Oyama 2022)
 *   - registerFamily → V1.5 navigation across casual/polite/formal variants
 *   - situationFamily → ladder pattern (A2/B1/B2 of same situation, validated)
 */

import type { Card } from '@kevvykevwin/lang-core/types';
import type { CEFRLevel } from '../types.js';
import type { L1Code } from '../i18n/types';
export type { L1Code };

// ============================================
// Categories
// ============================================

export type SituationCategory =
  | 'food-dining'
  | 'transport-directions'
  | 'social-smalltalk'
  | 'daily-life'
  | 'shopping'
  | 'health-medical'
  | 'work-workplace'
  | 'phone-comms'
  | 'emergencies-safety';

export const SITUATION_CATEGORIES: readonly SituationCategory[] = [
  'food-dining',
  'transport-directions',
  'social-smalltalk',
  'daily-life',
  'shopping',
  'health-medical',
  'work-workplace',
  'phone-comms',
  'emergencies-safety'
] as const;

/** Display labels for the category picker UI. */
export const CATEGORY_LABELS: Record<SituationCategory, string> = {
  'food-dining': 'Food & Dining',
  'transport-directions': 'Transportation & Directions',
  'social-smalltalk': 'Social Conversations & Small Talk',
  'daily-life': 'Daily Life & Routines',
  shopping: 'Shopping',
  'health-medical': 'Health & Medical',
  'work-workplace': 'Work & Workplace',
  'phone-comms': 'Phone & Digital Communication',
  'emergencies-safety': 'Emergencies & Safety'
};

// ============================================
// Speech-act taxonomy (Threshold Level 1990 + Biber lexical bundles)
//
// `KNOWN_SPEECH_ACTS` is a const array; the loader warns on unknown values
// at load time but does not fail. Content authors may introduce new values
// freely; promote frequently-used novel values to this array later.
// ============================================

export const KNOWN_SPEECH_ACTS = [
  'request',
  'refusal',
  'apology',
  'agreement',
  'disagreement',
  'opinion',
  'opening',
  'closing',
  'repair',
  'hedge',
  'gratitude',
  'complaint',
  'offering',
  'clarifying'
] as const;
export type SpeechAct = (typeof KNOWN_SPEECH_ACTS)[number];

export type Formality = 'casual' | 'polite' | 'formal';
export type Channel = 'in-person' | 'phone' | 'email' | 'chat';

// ============================================
// Card shape
// ============================================

export interface SituationCore {
  /** Stable id, dot-namespaced by category. e.g. "food-dining.check-please". */
  situationId: string;
  category: SituationCategory;
  cefr: CEFRLevel;

  /** L1 translations. `ja` is required (V1 audience); other L1s optional. */
  translations: { ja: string } & Partial<Record<Exclude<L1Code, 'ja'>, string>>;

  /** Pragmatic function tag — Lewis Lexical Approach + Wray + Biber. */
  speechAct?: SpeechAct;

  /**
   * Cross-link to other variants of the SAME communicative intent at
   * different formality levels (casual / polite / formal). V1 schema only;
   * navigation UI ships in V1.5.
   */
  registerFamily?: string;

  /**
   * Ladder cross-link — links variants of the same situation at different
   * CEFR levels. e.g. all "ordering coffee" variants share
   * `situationFamily: 'order-coffee'`. Validated at load via
   * `validateLadders()` to ensure ≥2 distinct CEFR levels per family.
   *
   * CONVENTION: all variants of one family co-locate in the same category file.
   */
  situationFamily?: string;

  /** General usage notes — keyed by language. Optional. */
  notes?: { en?: string; ja?: string };

  /**
   * Explicit English↔Japanese politeness-encoding mismatch callout —
   * surfaced with a 💡 badge when populated. Critical for J-L1 learners
   * because Japanese politeness is grammatical (です/ます) while English is
   * lexical (modal selection, hedging). Transfer error survives into
   * advanced learners (Oyama UCL 2022) — needs explicit instruction.
   */
  pragmaticNote?: { en?: string; ja?: string };

  /** 1 = monologue. 2 = two-person dialogue (renders both lines). */
  speakers?: 1 | 2;

  /** For 2-speaker dialogues: the partner line + its translation. */
  partner?: { en: string; ja: string };

  formality?: Formality;
  channel?: Channel;
  tags?: string[];
}

export type SituationCardExt = SituationCore;
export type SituationCard = Card<SituationCardExt>;
// Card<T> base provides: id, front (English phrase), back, audioText?,
// type?, tags?. For Situations: `back` may carry a kanji+kana variant
// (richer native reading) while `translations.ja` is the pure-kana display
// that the SituationsBrowser renders. Keep both — switching between them
// is a future per-user preference.

// ============================================
// Section guard (defends `english-section` localStorage value
// against schema drift across versions)
// ============================================

export type Section = 'flashcards' | 'quiz' | 'situations';

export const isSection = (v: unknown): v is Section =>
  v === 'flashcards' || v === 'quiz' || v === 'situations';
